import { SUPEROPS_CONFIG } from "../config/SuperOpsConfig";
import {
	getAppPathname,
	prependBasePath
} from "@/Utils/routing/getAppPathname";
import { SuperOpsState, StoredState } from "./interfaces";
import { OpportunityView } from "@/Contracts/Opportunity";
import store from "@/redux-store";
import { salesActivityModuleStore } from "@/SalesActivity/store/";
import SalesActivityActionCreators from "@/SalesActivity/store/actions/SalesActivity";
import OpportunityActionCreators from "@/Actions/Opportunity";
import BulkImportActionCreators from "@/Actions/BulkImport";
import DocumentsActionCreators from "@/Actions/Documents";
import { stateRegistry, SuperOpsStateKey } from "./StateRegistry";
import { RecentActivityFeedType } from "@/Contracts/Others/RecentActivityFeed";

class SuperOpsStateManager {
	private static RESTORE_KEY = SUPEROPS_CONFIG.STATE_STORAGE.KEY;
	private static EXPIRY_MINUTES =
		SUPEROPS_CONFIG.STATE_STORAGE.EXPIRY_MINUTES;

	saveRestoreStateForLater(state: SuperOpsState): void {
		try {
			const storedState: StoredState = {
				state,
				timestamp: Date.now(),
				expires:
					Date.now() + SuperOpsStateManager.EXPIRY_MINUTES * 60 * 1000
			};

			sessionStorage.setItem(
				SuperOpsStateManager.RESTORE_KEY,
				JSON.stringify(storedState)
			);
		} catch (error) {
			console.warn("Failed to save restore state:", error);
		}
	}

	getSavedRestoreState(): SuperOpsState | null {
		try {
			const saved = sessionStorage.getItem(
				SuperOpsStateManager.RESTORE_KEY
			);
			if (!saved) return null;

			const storedState: StoredState = JSON.parse(saved);

			// Check if expired
			if (Date.now() > storedState.expires) {
				this.clearSavedRestoreState();
				return null;
			}

			return storedState.state;
		} catch (error) {
			console.warn("Failed to retrieve restore state:", error);
			this.clearSavedRestoreState();
			return null;
		}
	}

	clearSavedRestoreState(): void {
		try {
			sessionStorage.removeItem(SuperOpsStateManager.RESTORE_KEY);
		} catch (error) {
			console.warn("Failed to clear restore state:", error);
		}
	}

	async restoreState(state: SuperOpsState): Promise<void> {
		try {
			// 1. Navigate to route if different
			const stateRoutePathname = state.route.split("?")[0];
			if (state.route && getAppPathname() !== stateRoutePathname) {
				await this.navigateToRoute(state.route);
			}

			// 2. Restore local states first (before Redux)
			if (state.localStates) {
				this.restoreLocalStates(state.localStates);
			}

			// 3. Restore opportunity-specific state
			if (state.opportunity) {
				this.restoreOpportunityState(state.opportunity);
			}

			// 4. Restore document-specific state
			if (state.document) {
				this.restoreDocumentState(state.document);
			}

			// 5. Restore bulk import state
			if (state.bulkImport) {
				this.restoreBulkImportState(state.bulkImport);
			}

			// 6. Restore sales activity state
			if (state.salesActivity) {
				this.restoreSalesActivityState(state.salesActivity);
			}
		} catch (error) {
			console.error("Failed to restore state:", error);
			throw error;
		}
	}

	getCurrentState(): SuperOpsState {
		// Get current route and add pipeline_id if available
		let route = getAppPathname() + window.location.search;

		try {
			const state = store.getState();
			const currentActivePipeline = state.pipelines.currentActivePipeline;
			const currentActiveOpportunity =
				state.opportunities.currentActiveOpportunity;
			const currentActiveDocument = state.documents.currentActiveDocument;

			if (currentActivePipeline?.id) {
				const url = new URL(route, window.location.origin);
				url.searchParams.set("pipeline_id", currentActivePipeline.id);
				route = url.pathname + url.search;
			}
			if (currentActiveOpportunity?.id) {
				const url = new URL(route, window.location.origin);
				url.searchParams.set(
					"opportunity_id",
					currentActiveOpportunity.id
				);
				route = url.pathname + url.search;
			}
			if (currentActiveDocument?.id) {
				const url = new URL(route, window.location.origin);
				url.searchParams.set("document_id", currentActiveDocument.id);
				route = url.pathname + url.search;
			}
			if (getAppPathname().includes("/opportunities")) {
				const salesActivityActivityState =
					salesActivityModuleStore.getState();
				const salesActivityActiveTab =
					salesActivityActivityState.salesActivity
						.salesActivityActiveTab;
				const currentActiveSalesActivity =
					salesActivityActivityState.salesActivity
						.currentActiveSalesActivity;
				const url = new URL(route, window.location.origin);
				url.searchParams.set(
					"sales_activity_tab",
					(salesActivityActiveTab as RecentActivityFeedType) ||
						RecentActivityFeedType.All
				);
				if (currentActiveSalesActivity?.id) {
					url.searchParams.set(
						"sales_activity_id",
						currentActiveSalesActivity.id
					);
				}
				route = url.pathname + url.search;
			}
		} catch (error) {
			console.warn("Failed to add ids to route:", error);
		}

		return {
			route,
			opportunity: this.getCurrentOpportunityState(),
			document: this.getCurrentDocumentState(),
			bulkImport: this.getCurrentBulkImportState(),
			salesActivity: this.getCurrentSalesActivityState(),
			localStates: stateRegistry.getAll()
		};
	}

	checkForPendingRestoration(): void {
		const savedState = this.getSavedRestoreState();
		if (savedState) {
			this.restoreState(savedState)
				.then(() => {
					this.clearSavedRestoreState();
				})
				.catch(error => {
					console.error(
						"Failed to apply pending restoration:",
						error
					);
					this.clearSavedRestoreState();
				});
		}
	}

	private navigateToRoute(route: string): Promise<void> {
		return new Promise(resolve => {
			window.history.pushState(null, "", prependBasePath(route));
			// Trigger route change event for React Router
			const event = new PopStateEvent("popstate");
			window.dispatchEvent(event);
			// Wait for route change to complete
			setTimeout(resolve, 50);
		});
	}

	private restoreOpportunityState(
		opportunityState: NonNullable<SuperOpsState["opportunity"]>
	): void {
		try {
			store.dispatch(
				OpportunityActionCreators.restoreOpportunityState(
					opportunityState
				)
			);
		} catch (error) {
			console.warn("Failed to restore opportunity state:", error);
		}
	}

	private restoreDocumentState(
		documentState: NonNullable<SuperOpsState["document"]>
	): void {
		try {
			if (documentState.currentActiveView) {
				store.dispatch(
					DocumentsActionCreators.updateCurrentActiveView(
						documentState.currentActiveView
					)
				);
			}
			if (documentState.editOrCloneDocumentDrawerOpen !== undefined) {
				if (documentState.editOrCloneDocumentDrawerOpen) {
					store.dispatch(
						DocumentsActionCreators.editOrCloneDocumentDrawerOpened()
					);
				} else {
					store.dispatch(
						DocumentsActionCreators.editOrCloneDocumentDrawerClosed()
					);
				}
			}
		} catch (error) {
			console.warn("Failed to restore document state:", error);
		}
	}

	private restoreBulkImportState(
		bulkImportState: NonNullable<SuperOpsState["bulkImport"]>
	): void {
		try {
			store.dispatch(
				BulkImportActionCreators.restoreBulkImportState(bulkImportState)
			);
		} catch (error) {
			console.warn("Failed to restore bulk import state:", error);
		}
	}

	private restoreLocalStates(localStates: Record<string, any>): void {
		try {
			// Populate the registry with saved local states
			// Components will pick these up when they mount
			Object.entries(localStates).forEach(([key, value]) => {
				// Validate that the key is a valid SuperOpsStateKey enum value
				if (
					Object.values(SuperOpsStateKey).includes(
						key as SuperOpsStateKey
					)
				) {
					stateRegistry.register(key as SuperOpsStateKey, value);
				}
			});
		} catch (error) {
			console.warn("Failed to restore local states:", error);
		}
	}

	private restoreSalesActivityState(
		salesActivityState: NonNullable<SuperOpsState["salesActivity"]>
	): void {
		try {
			// Restore global add comment drawer state
			if (salesActivityState.globalAddCommentDrawerOpen !== undefined) {
				if (salesActivityState.globalAddCommentDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalCommentDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalCommentDrawerClosed()
					);
				}
			}

			// Restore global add task drawer state
			if (salesActivityState.globalAddTaskDrawerOpen !== undefined) {
				if (salesActivityState.globalAddTaskDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalTaskDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalTaskDrawerClosed()
					);
				}
			}

			// Restore global add meeting drawer state
			if (salesActivityState.globalAddMeetingDrawerOpen !== undefined) {
				if (salesActivityState.globalAddMeetingDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalMeetingDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalMeetingDrawerClosed()
					);
				}
			}

			// Restore global add log call drawer state
			if (salesActivityState.globalAddLogCallDrawerOpen !== undefined) {
				if (salesActivityState.globalAddLogCallDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalLogCallDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalLogCallDrawerClosed()
					);
				}
			}

			// Restore global add log email drawer state
			if (salesActivityState.globalAddLogEmailDrawerOpen !== undefined) {
				if (salesActivityState.globalAddLogEmailDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalLogEmailDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.addGlobalLogEmailDrawerClosed()
					);
				}
			}

			// Restore global edit task drawer state
			if (salesActivityState.globalEditTaskDrawerOpen !== undefined) {
				if (salesActivityState.globalEditTaskDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalTaskDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalTaskDrawerClosed()
					);
				}
			}

			// Restore global edit log email drawer state
			if (salesActivityState.globalEditLogEmailDrawerOpen !== undefined) {
				if (salesActivityState.globalEditLogEmailDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalLogEmailDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalLogEmailDrawerClosed()
					);
				}
			}

			// Restore global edit log call drawer state
			if (salesActivityState.globalEditLogCallDrawerOpen !== undefined) {
				if (salesActivityState.globalEditLogCallDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalLogCallDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalLogCallDrawerClosed()
					);
				}
			}

			// Restore global edit meeting drawer state
			if (salesActivityState.globalEditMeetingDrawerOpen !== undefined) {
				if (salesActivityState.globalEditMeetingDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalMeetingDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalMeetingDrawerClosed()
					);
				}
			}

			// Restore global edit comment drawer state
			if (salesActivityState.globalEditCommentDrawerOpen !== undefined) {
				if (salesActivityState.globalEditCommentDrawerOpen) {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalCommentDrawerOpened()
					);
				} else {
					salesActivityModuleStore.dispatch(
						SalesActivityActionCreators.editGlobalCommentDrawerClosed()
					);
				}
			}
		} catch (error) {
			console.warn("Failed to restore sales activity state:", error);
		}
	}

	// State getter methods - these would need to be implemented based on actual app structure

	private getCurrentOpportunityState(): SuperOpsState["opportunity"] {
		// Opportunity data is only relevant for opportunity pages
		if (!getAppPathname().includes("/opportunities")) {
			return undefined;
		}

		try {
			const state = store.getState();
			const oppState = state.opportunities;

			return {
				viewMode:
					oppState.activeOpportunityView === OpportunityView.List
						? "list"
						: "kanban",
				currentActiveView: oppState.currentActiveView || undefined,
				addDrawerOpen: oppState.addOpportunityDrawerOpen,
				editDrawerOpen: oppState.editOpportunityDrawerOpen
			};
		} catch (error) {
			console.warn("Failed to get opportunity state from Redux:", error);
			return undefined;
		}
	}

	private getCurrentDocumentState(): SuperOpsState["document"] {
		// Document data is only relevant for document pages
		if (!getAppPathname().includes("/documents")) {
			return undefined;
		}

		try {
			const state = store.getState();
			const docState = state.documents;

			return {
				currentActiveView: docState.currentActiveView || undefined,
				editOrCloneDocumentDrawerOpen:
					docState.editOrCloneDocumentDrawerOpen
			};
		} catch (error) {
			console.warn("Failed to get document state from Redux:", error);
			return undefined;
		}
	}

	private getCurrentBulkImportState(): SuperOpsState["bulkImport"] {
		// Bulk import state is relevant for bulk import pages
		if (!getAppPathname().includes("/import/opportunity")) {
			return undefined;
		}

		try {
			const state = store.getState();
			const bulkImportState = state.bulkImport;

			return {
				spreadsheetBulkImportDrawerOpen:
					bulkImportState.spreadsheetBulkImportDrawerOpen
			};
		} catch (error) {
			console.warn("Failed to get bulk import state from Redux:", error);
			return undefined;
		}
	}

	private getCurrentSalesActivityState(): SuperOpsState["salesActivity"] {
		try {
			const salesActivityState = salesActivityModuleStore.getState();

			return {
				globalAddCommentDrawerOpen:
					salesActivityState.salesActivity.globalAddCommentDrawerOpen,
				globalAddTaskDrawerOpen:
					salesActivityState.salesActivity.globalAddTaskDrawerOpen,
				globalAddMeetingDrawerOpen:
					salesActivityState.salesActivity.globalAddMeetingDrawerOpen,
				globalAddLogCallDrawerOpen:
					salesActivityState.salesActivity.globalAddLogCallDrawerOpen,
				globalAddLogEmailDrawerOpen:
					salesActivityState.salesActivity
						.globalAddLogEmailDrawerOpen,
				globalEditTaskDrawerOpen:
					salesActivityState.salesActivity.globalEditTaskDrawerOpen,
				globalEditLogEmailDrawerOpen:
					salesActivityState.salesActivity
						.globalEditLogEmailDrawerOpen,
				globalEditLogCallDrawerOpen:
					salesActivityState.salesActivity
						.globalEditLogCallDrawerOpen,
				globalEditMeetingDrawerOpen:
					salesActivityState.salesActivity
						.globalEditMeetingDrawerOpen,
				globalEditCommentDrawerOpen:
					salesActivityState.salesActivity.globalEditCommentDrawerOpen
			};
		} catch (error) {
			console.warn("Failed to get sales activity state:", error);
			return undefined;
		}
	}
}

export { SuperOpsStateManager };
